import {
  CheckOutlined,
  CloseOutlined,
  PaperClipOutlined,
  PlusOutlined
} from "@ant-design/icons"
import { App, Button, DatePicker, Flex, Form, Input, Tag, Tooltip } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { type ReactNode, useState } from "react"

import { INVOICE_COLUMN_KEYS } from "./columns.tsx"
import { buildEditRowCells, rowCommitKeyDown } from "../../../../../../../components/EntityTable/editable-row-cells.ts"
import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { FormChangedWatcher } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import InputNumber from "../../../../../../../components/Input/InputNumber.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { dateAutoFill } from "../../../../../../../utils/form/date-auto-fill.tsx"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import {
  INVOICE_STATUSES,
  INVOICE_STATUSES_MAP,
  type InvoiceStatuses
} from "../../../../constants/invoice-statuses.ts"
import type { ProjectInvoice } from "../../../../../types/project-invoices.types.tsx"
import { ProjectViewInvoiceAssigneeSelect } from "./ProjectViewInvoiceAssigneeSelect.tsx"

interface ProjectViewInvoiceNewRowProps {
  // rc-table hands the default cells to a body-row component; the edit row
  // matches its own cells against them by column key.
  children?: ReactNode
  record: ProjectInvoice
  options: Record<string, BaseOptionType[]>
  mode?: "create" | "edit"
  selfAssigned: boolean
  onSelectRow: (record: ProjectInvoice, action: string) => Promise<void> | void
}

export const ProjectViewInvoiceNewRow = ({
  record,
  onSelectRow,
  options,
  selfAssigned,
  mode = "create",
  ...rest
}: ProjectViewInvoiceNewRowProps) => {
  const { getDateValueProps, createDateRangeRules, normalizeDate } =
    useDateFormat()
  const isCreate = mode === "create"

  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectInvoice>()
  const { modal } = App.useApp()

  const handleSave = async () => {
    try {
      const { id, ...initialData } = record
      const values = await validateFormWithScroll(form)
      const action = isCreate ? "create" : "update"

      setSaving(true)
      await onSelectRow(
        {
          // Echoing the whole row back would resend `lines`, which the backend
          // reads as a line replacement and refuses outside generated drafts.
          ...(isCreate ? initialData : { id }),
          ...values,
          date_issued: normalizeDate(values?.date_issued),
          due_date: normalizeDate(values?.due_date)
        },
        action
      )
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const action = isCreate ? "cancel_create" : "cancel"

    const isChanged = isFormChanged<ProjectInvoice>(
      form.getFieldsValue(),
      record
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          onSelectRow(record, action)
        }
      })
    } else {
      onSelectRow(record, action)
    }
  }

  const rowClassName = isCreate ? "bg-blue-50" : ""

  // Status is derived from payments server-side; the row only reports it.
  const displayedStatus = (
    isCreate ? INVOICE_STATUSES.ISSUED : record?.status
  ) as InvoiceStatuses | undefined
  const statusDisplay = displayedStatus
    ? INVOICE_STATUSES_MAP[displayedStatus]
    : undefined

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    busy: saving
  })

  return (
    <Form<ProjectInvoice>
      form={form}
      component={false}
      initialValues={record}
      disabled={saving}
    >
      <FormChangedWatcher<ProjectInvoice> form={form} saving={saving} />

      <tr {...rest} onKeyDown={onKeyDownRow} className={rowClassName}>
        {buildEditRowCells(
          rest?.children,
          Object.values(INVOICE_COLUMN_KEYS),
          {
            [INVOICE_COLUMN_KEYS.invoiceNumber]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectInvoice> name="type" noStyle hidden>
                  <Input />
                </Form.Item>
                <Form.Item<ProjectInvoice> name="service_type" noStyle hidden>
                  <Input />
                </Form.Item>
                <Form.Item<ProjectInvoice>
                  name="invoice_number"
                  noStyle
                  rules={[
                    { required: true, message: "Required" },
                    {
                      max: 50,
                      message: "Invoice must be at most 50 characters long"
                    }
                  ]}
                >
                  <Input.TextArea
                    autoSize
                    placeholder="Invoice ID"
                    autoFocus={isCreate}
                  />
                </Form.Item>
              </td>
            ),
            [INVOICE_COLUMN_KEYS.customerName]: (
              <td className="ant-table-cell">{record?.customer_name || "—"}</td>
            ),
            [INVOICE_COLUMN_KEYS.dateIssued]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectInvoice>
                  name="date_issued"
                  noStyle
                  getValueProps={getDateValueProps}
                  rules={[
                    { required: true, message: "Required" },
                    ...createDateRangeRules<ProjectInvoice>("due_date", "start")
                  ]}
                  dependencies={["due_date"]}
                >
                  <DatePicker
                    className="w-full"
                    onChange={dateAutoFill<ProjectInvoice>({
                      form,
                      endDatePath: ["due_date"]
                    })}
                  />
                </Form.Item>
              </td>
            ),
            [INVOICE_COLUMN_KEYS.dueDate]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectInvoice>
                  name="due_date"
                  noStyle
                  getValueProps={getDateValueProps}
                  rules={[
                    { required: true, message: "Required" },
                    ...createDateRangeRules<ProjectInvoice>("date_issued", "end")
                  ]}
                  dependencies={["date_issued"]}
                >
                  <DatePicker className="w-full" />
                </Form.Item>
              </td>
            ),
            [INVOICE_COLUMN_KEYS.amount]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectInvoice>
                  name="amount"
                  noStyle
                  rules={[{ required: true, message: "Required" }]}
                >
                  <InputNumber
                    min={0}
                    className="w-full"
                    prefix="$"
                    placeholder="0.00"
                  />
                </Form.Item>
              </td>
            ),
            [INVOICE_COLUMN_KEYS.originDisplay]: (
              <td className="ant-table-cell">{"—"}</td>
            ),
            [INVOICE_COLUMN_KEYS.paidToDate]: (
              <td className="ant-table-cell">{"—"}</td>
            ),
            [INVOICE_COLUMN_KEYS.status]: (
              <td className="ant-table-cell">
                {statusDisplay ? (
                  <Tag style={statusDisplay.style} className="m-0">
                    {statusDisplay.label}
                  </Tag>
                ) : (
                  "—"
                )}
              </td>
            ),
            [INVOICE_COLUMN_KEYS.assigneeName]: (
              <td className="ant-table-cell">
                <ProjectViewInvoiceAssigneeSelect
                  form={form}
                  disabled={selfAssigned}
                  options={options}
                />
              </td>
            ),
            [INVOICE_COLUMN_KEYS.attachments]: (
              <td className="ant-table-cell text-center">
                <Tooltip title="Only available in view mode">
                  <Button disabled icon={<PaperClipOutlined />} />
                </Tooltip>
              </td>
            )
          },
          <td
            className={`sticky right-0 ant-table-cell ant-table-cell-fix-right ant-table-cell-fix-right-first ${rowClassName}`}
          >
            <Flex gap="small" justify="center">
              <Button
                variant="outlined"
                color="danger"
                onClick={handleCancel}
                icon={<CloseOutlined />}
              />
              <Button
                loading={saving}
                variant="outlined"
                color="green"
                onClick={handleSave}
                icon={isCreate ? <PlusOutlined /> : <CheckOutlined />}
              />
            </Flex>
          </td>
        )}
      </tr>
    </Form>
  )
}
